/**
 * Unifies asset filtering and bulk approval controls with the shared table action-bar
 * layout while keeping URL state and asset mutations in the owning container.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import gridTokens from '@amzn/meridian-tokens/base/icon/grid';
import imageTokens from '@amzn/meridian-tokens/base/icon/image';
import { useTranslation } from 'src/arb-wrapper';
import { AssetActionButton } from 'src/components/assets/AssetActionButton';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { AssetsActionBarFilters } from 'src/components/assets/AssetsActionBarFilters';
import { type AssetStatus, ASSET_STATUSES } from 'src/components/assets/asset-status';
import { DataTableActionBar } from 'src/components/common/data-table-action-bar/DataTableActionBar';
import { PUBLISHER_ALL } from 'src/utils/assetFilterUtils';

interface AssetActionBarBaseProps {
  publisher: string;
  campaigns: string[];
  channel: string;
  size: string;
  locale: string;
  country: string;
  assetType: string;
  liveStatus: string;
  businessLine: string;
  publishers: string[];
  campaignOptions: Array<{ id: string; name: string }>;
  availableChannels: string[];
  availableSizes: string[];
  availableLiveStatuses: string[];
  isChannelsFetching: boolean;
  isSizesFetching: boolean;
  titleSearch?: string;
  strategy: string;
  strategyOptions: Array<{ value: string; label: string }>;
  approvalStatus: AssetStatus;
  selectedCount: number;
  hasAssets: boolean;
  selectAll: boolean;
  actionDisabled: boolean;
  showSelectionControls: boolean;
  bulkScheduleAction?: {
    disabled: boolean;
    onClick: () => void;
  };
  onPublisherChange: (value: string) => void;
  onCampaignsChange: (value: string[]) => void;
  onChannelChange: (value: string) => void;
  onSizeChange: (value: string) => void;
  onLocaleChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onAssetTypeChange: (value: string) => void;
  onLiveStatusChange: (value: string) => void;
  onBusinessLineChange: (value: string) => void;
  onStrategyChange: (value: string) => void;
  onTitleSearchChange?: (value: string) => void;
  onClearFilters: () => void;
  onToggleSelectAll: () => void;
  onToggleDetailView: () => void;
  isDetailView: boolean;
  onActionClick: (action: AssetStatus) => Promise<void>;
}

export type AssetsActionBarProps = AssetActionBarBaseProps & (
  | { gti?: undefined; onGtiChange?: never }
  | { gti: string; onGtiChange: (value: string) => void }
);

export const AssetsActionBar: React.FC<AssetsActionBarProps> = (props) => {
  const { t } = useTranslation();
  const canApproveAssets = useCanPerform('APPROVE_ASSETS');

  const hasActiveFilters = props.publisher !== PUBLISHER_ALL || props.campaigns.length > 0 ||
    [props.channel, props.size, props.locale, props.country, props.assetType, props.liveStatus,
      props.businessLine, props.strategy, props.gti, props.titleSearch].some(Boolean);
  const getCampaignName = (id: string) =>
    props.campaignOptions.find((campaign) => campaign.id === id)?.name ?? id;
  const getStrategyLabel = (value: string) =>
    props.strategyOptions.find((option) => option.value === value)?.label ?? value;

  return (
    <DataTableActionBar
      actions={(
        <>
          {props.bulkScheduleAction && props.showSelectionControls && (
            <Button
              data-testid="bulk-schedule-edit-button"
              disabled={props.bulkScheduleAction.disabled}
              type="secondary"
              onClick={props.bulkScheduleAction.onClick}
            >
              {t('asset_schedule_bulk_edit_button')}
            </Button>
          )}
          {!props.showSelectionControls && props.approvalStatus === ASSET_STATUSES.PENDING && canApproveAssets
            ? (
            <>
              <Button
                data-testid="asset-action-button-approved"
                disabled={props.actionDisabled}
                type="primary"
                onClick={() => { props.onActionClick(ASSET_STATUSES.APPROVED); }}
              >
                {t('asset_action_approve')}
              </Button>
              <Button
                data-testid="asset-action-button-rejected"
                disabled={props.actionDisabled}
                type="primary"
                onClick={() => { props.onActionClick(ASSET_STATUSES.REJECTED); }}
              >
                {t('asset_action_reject')}
              </Button>
            </>
              )
            : canApproveAssets
              ? (
            <AssetActionButton
              approvalStatus={props.approvalStatus}
              disabled={props.actionDisabled}
              onActionClick={props.onActionClick}
            />
                )
              : null
          }
        </>
      )}
      bulkContent={!props.showSelectionControls
        ? null
        : props.hasAssets
          ? (
        <>
          <Text data-testid="asset-selected-count" type="b200">
            {t('bulk_edit_count', { count: props.selectedCount })}
          </Text>
          <Button
            data-testid={`assets-select-all-checkbox-${props.approvalStatus.toLowerCase()}`}
            type="link"
            onClick={props.onToggleSelectAll}
          >
            {props.approvalStatus === ASSET_STATUSES.PENDING
              ? t(props.selectAll ? 'mark_all_as_rejected' : 'mark_all_as_approved')
              : t(props.selectAll ? 'unselect_all' : 'select_all')}
          </Button>
        </>
            )
          : (
        <Text color="secondary" type="b300">
          {t('select_row_to_view_bulk_actions')}
        </Text>
            )}
      filterButtonLabel={t('filter_assets_header')}
      filterButtonTestId="asset-filter-button"
      filterContent={hasActiveFilters
        ? (
        <>
          {props.publisher !== PUBLISHER_ALL && (
            <Tag data-testid="filter-tag-publisher" type="theme" onClose={() => { props.onPublisherChange(PUBLISHER_ALL); }}>
              {`Publisher: ${props.publisher}`}
            </Tag>
          )}
          {props.businessLine && (
            <Tag data-testid="filter-tag-business-line" type="theme" onClose={() => { props.onBusinessLineChange(''); }}>
              {`${t('business_line')}: ${props.businessLine}`}
            </Tag>
          )}
          {props.campaigns.map((id) => (
            <Tag data-testid={`filter-tag-campaign-${id}`} key={id} type="theme" onClose={() => { props.onCampaignsChange(props.campaigns.filter((campaign) => campaign !== id)); }}>
              {`Campaign: ${getCampaignName(id)}`}
            </Tag>
          ))}
          {props.channel && (
            <Tag data-testid="filter-tag-channel" type="theme" onClose={() => { props.onChannelChange(''); }}>
              {`Channel: ${props.channel === 'no-channel' ? t('no_channel') : props.channel}`}
            </Tag>
          )}
          {props.size && <Tag data-testid="filter-tag-size" type="theme" onClose={() => { props.onSizeChange(''); }}>{`Size: ${props.size}`}</Tag>}
          {props.locale && <Tag data-testid="filter-tag-locale" type="theme" onClose={() => { props.onLocaleChange(''); }}>{`Locale: ${props.locale}`}</Tag>}
          {props.country && <Tag data-testid="filter-tag-country" type="theme" onClose={() => { props.onCountryChange(''); }}>{`Country: ${props.country}`}</Tag>}
          {props.assetType && <Tag data-testid="filter-tag-asset-type" type="theme" onClose={() => { props.onAssetTypeChange(''); }}>{`Type: ${props.assetType}`}</Tag>}
          {props.liveStatus && <Tag data-testid="filter-tag-live-status" type="theme" onClose={() => { props.onLiveStatusChange(''); }}>{`Live Status: ${props.liveStatus}`}</Tag>}
          {props.strategy && (
            <Tag data-testid="filter-tag-strategy" type="theme" onClose={() => { props.onStrategyChange(''); }}>
              {`${t('product_strategy_label')}: ${getStrategyLabel(props.strategy)}`}
            </Tag>
          )}
          {props.gti && <Tag data-testid="filter-tag-gti" type="theme" onClose={() => { props.onGtiChange?.(''); }}>{`GTI: ${props.gti}`}</Tag>}
          {props.titleSearch && (
            <Tag data-testid="filter-tag-title-search" type="theme" onClose={() => { props.onTitleSearchChange?.(''); }}>
              {`${t('search_by_title')}: ${props.titleSearch}`}
            </Tag>
          )}
          <Button data-testid="clear-filters-button" size="small" type="link" onClick={props.onClearFilters}>
            {t('clear_filters')}
          </Button>
        </>
          )
        : <Text color="secondary" type="b300">{t('no_filters_applied')}</Text>}
      filterPopoverContent={<AssetsActionBarFilters {...props} />}
      search={{
        value: props.titleSearch ?? '',
        placeholder: t('search_by_title'),
        testId: 'asset-title-search',
        width: 350,
        onChange: (value) => { props.onTitleSearchChange?.(value); },
      }}
      trailingFilterContent={(
        <Button
          aria-label={t(props.isDetailView ? 'asset_view_grid' : 'asset_view_detail')}
          data-testid="assets-view-mode-toggle"
          type="icon"
          onClick={props.onToggleDetailView}
        >
          <Icon tokens={props.isDetailView ? imageTokens : gridTokens}>
            {t(props.isDetailView ? 'asset_view_grid' : 'asset_view_detail')}
          </Icon>
        </Button>
      )}
      onClearFilters={props.onClearFilters}
    />
  );
};
